import type { PropsWithChildren } from 'react';
import { createContext, useCallback, useContext } from 'react';
import type {
  GoogleAnalyticsEventParameter,
  ValueMapping
} from '@elfsight/lib-google-analytics';
import { sendGoogleAnalyticsEvents } from '@elfsight/lib-google-analytics';
import type { WidgetBackgroundProps } from '@elfsight/widget-ui';
import { widgetBackgroundDefaultSettings } from '@elfsight/widget-ui';
import type {
  GoogleAnalytics,
  GoogleAnalyticsEventParameterType,
  GoogleAnalyticsEventValueType
} from '../types';
import { Layout } from '../types';
import type { BannerProps } from './Banner';
import { bannerSettings } from './bannerSettings';

type BannerProviderProps = PropsWithChildren<{
  blocks: BannerProps['blocks'];
  widgetId: string;
  googleAnalytics?: GoogleAnalytics;
  googleAnalyticsExtraParameters?: GoogleAnalyticsEventParameter;
  contentSpacing: NonNullable<BannerProps['contentSpacing']>;
  contentDirection: NonNullable<BannerProps['contentDirection']>;
  contentHorizontalPadding: NonNullable<
    BannerProps['contentHorizontalPadding']
  >;
  contentVerticalPadding: NonNullable<BannerProps['contentVerticalPadding']>;
  stretchBackground: NonNullable<BannerProps['stretchBackground']>;
  borderRadius: WidgetBackgroundProps['borderRadius'];
  outerMargin: number;
  mobileView: boolean;
  canPlayMedia?: boolean;
}>;

type BannerContextType = Omit<BannerProviderProps, 'blocks' | 'editMode'> & {
  hasBannerBlocks: boolean;
  trackGoogleAnalyticsEvents?: (args: TrackArgs) => void;
};

type TrackArgs<
  V extends GoogleAnalyticsEventValueType = GoogleAnalyticsEventValueType
> = {
  eventType: V;
  valueMapping?: Partial<ValueMapping<V | GoogleAnalyticsEventParameterType>>;
};

const BannerContext = createContext<BannerContextType>({
  contentSpacing: bannerSettings.contentSpacing,
  contentDirection: bannerSettings.contentDirection,
  contentHorizontalPadding: bannerSettings.contentPadding,
  contentVerticalPadding: bannerSettings.contentPadding,
  stretchBackground: bannerSettings.stretchBackground,
  borderRadius: widgetBackgroundDefaultSettings.widgetBackground.borderRadius,
  outerMargin: 0,
  hasBannerBlocks: false,
  widgetId: 'widget-id',
  mobileView: false
});

const MEDIA_BANNER_LAYOUTS: Layout[] = [
  Layout.EmbeddedBanner,
  Layout.LeftBanner,
  Layout.RightBanner
];

export function BannerProvider({
  children,
  blocks,
  googleAnalytics,
  googleAnalyticsExtraParameters,
  widgetId,
  ...forwardingProps
}: BannerProviderProps) {
  const hasBannerBlocks =
    blocks?.some((block) => {
      if ('layout' in block && !!block.layout) {
        const { type, layout } = block;

        if (type === 'image' || type === 'video') {
          return MEDIA_BANNER_LAYOUTS.includes(layout);
        }
      }

      return false;
    }) || false;

  const trackGoogleAnalyticsEvents = useCallback(
    ({ eventType, valueMapping }: TrackArgs) => {
      if (!googleAnalytics || googleAnalytics.length === 0) {
        return;
      }

      sendGoogleAnalyticsEvents({
        googleAnalyticsEvents: googleAnalytics,
        eventType,
        valueMapping: {
          widgetId,
          ...valueMapping,
          ...googleAnalyticsExtraParameters
        }
      });
    },
    [googleAnalytics, googleAnalyticsExtraParameters, widgetId]
  );

  return (
    <BannerContext.Provider
      value={{
        ...forwardingProps,
        hasBannerBlocks,
        widgetId,
        trackGoogleAnalyticsEvents
      }}
    >
      {children}
    </BannerContext.Provider>
  );
}

export function useBanner() {
  const context = useContext(BannerContext);

  if (!context) {
    throw new Error('useBanner must be used within the BannerProvider');
  }

  return context;
}
